import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserPlus, UserMinus, Users, ShieldCheck, Eye, Wrench, Loader2, Building2 } from "lucide-react";
import { Navigate } from "react-router-dom";
import { useState, useMemo } from "react";
import { useRegisterHeaderAction } from "@/hooks/useRegisterHeaderAction";
import { InviteMemberModal } from "@/components/InviteMemberModal";
import { OrgSettingsModal } from "@/components/OrgSettingsModal";
import { toast } from "sonner";

const ROLE_CONFIG: Record<string, { label: string; icon: typeof ShieldCheck; className: string }> = {
  ADMIN: { label: "Admin", icon: ShieldCheck, className: "bg-primary/15 text-primary border-primary/30" },
  OPERADOR: { label: "Operador", icon: Wrench, className: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
  VISOR: { label: "Visor", icon: Eye, className: "bg-muted text-muted-foreground border-border" },
};

interface MemberRow {
  id: string;
  user_id: string;
  rol: string;
  created_at: string;
  email: string | null;
  full_name: string | null;
  status?: string;
}

export default function Equipo() {
  const { orgId, userRole, user, memberships } = useAuth();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [orgSettingsOpen, setOrgSettingsOpen] = useState(false);
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const [confirmTarget, setConfirmTarget] = useState<MemberRow | null>(null);
  const queryClient = useQueryClient();

  const canEditOrg = userRole === "ADMIN" || userRole === "OWNER";
  const activeOrg = memberships.find((m) => m.org_id === orgId);

  const headerAction = useMemo(
    () => (
      <div className="flex items-center gap-2">
        {canEditOrg && (
          <Button
            variant="outline"
            onClick={() => setOrgSettingsOpen(true)}
            className="gap-2"
          >
            <Building2 className="h-4 w-4" />
            Ajustes de Empresa
          </Button>
        )}
        <Button onClick={() => setInviteOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2">
          <UserPlus className="h-4 w-4" />
          Invitar Miembro
        </Button>
      </div>
    ),
    [canEditOrg]
  );
  useRegisterHeaderAction(headerAction);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["team-members", orgId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_org_members_with_email", {
        _org_id: orgId!,
      });
      if (error) throw error;

      const { data: statusData } = await supabase
        .from("miembros_organizacion")
        .select("id, status")
        .eq("org_id", orgId!);

      const statusMap = new Map((statusData ?? []).map((s: any) => [s.id, s.status]));

      return ((data ?? []) as MemberRow[]).map((m) => ({
        ...m,
        status: (statusMap.get(m.id) as string) ?? "active",
      }));
    },
    enabled: !!orgId,
  });

  const toggleStatus = useMutation({
    mutationFn: async ({ memberId, newStatus }: { memberId: string; newStatus: string }) => {
      const { error } = await supabase
        .from("miembros_organizacion")
        .update({ status: newStatus } as any)
        .eq("id", memberId);
      if (error) throw error;
    },
    onSuccess: (_, { newStatus }) => {
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      toast.success(newStatus === "active" ? "Miembro reactivado" : "Miembro desactivado");
      setConfirmTarget(null);
    },
    onError: () => {
      toast.error("Error al actualizar el estado del miembro");
      setConfirmTarget(null);
    },
  });

  if (userRole !== "ADMIN") {
    return <Navigate to="/dashboard/embarques" replace />;
  }

  const getDisplayName = (m: { full_name: string | null; email: string | null }) =>
    (m.full_name?.trim() || m.email?.split("@")[0] || "Sin correo");

  const getInitials = (m: { full_name: string | null; email: string | null }) => {
    const src = (m.full_name?.trim() || m.email || "").trim();
    if (!src) return "?";
    const parts = src.split(/\s+|[._@-]/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return src.substring(0, 2).toUpperCase();
  };

  const handleToggleClick = (member: MemberRow) => {
    if (member.user_id === user?.id) {
      toast.error("No puedes desactivar tu propia cuenta de administrador");
      return;
    }
    if (member.status === "active") {
      setConfirmTarget(member);
    } else {
      toggleStatus.mutate({ memberId: member.id, newStatus: "active" });
    }
  };

  const handleConfirmDeactivate = () => {
    if (!confirmTarget) return;
    toggleStatus.mutate({ memberId: confirmTarget.id, newStatus: "inactive" });
  };

  const visibleMembers = showOnlyActive
    ? members.filter((m) => m.status !== "inactive")
    : members;

  const inactiveCount = members.filter((m) => m.status === "inactive").length;

  return (
    <main className="flex-1 p-6 space-y-6 overflow-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-lg font-semibold text-foreground">Mi Equipo</h2>
            <p className="text-sm text-muted-foreground">
              {members.length} miembro{members.length !== 1 ? "s" : ""}
              {inactiveCount > 0 && ` · ${inactiveCount} inactivo${inactiveCount !== 1 ? "s" : ""}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {inactiveCount > 0 && (
            <div className="flex items-center gap-2">
              <Switch
                id="filter-active"
                checked={showOnlyActive}
                onCheckedChange={setShowOnlyActive}
              />
              <Label htmlFor="filter-active" className="text-sm text-muted-foreground cursor-pointer whitespace-nowrap">
                Solo activos
              </Label>
            </div>
          )}
          <Button className="gap-2" onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-4 w-4" />
            Invitar Miembro
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuario</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Miembro desde</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-12">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                  Cargando equipo...
                </TableCell>
              </TableRow>
            ) : visibleMembers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-12">
                  {showOnlyActive && inactiveCount > 0
                    ? "Todos los miembros están inactivos. Desactiva el filtro para verlos."
                    : "Sin miembros"}
                </TableCell>
              </TableRow>
            ) : (
              visibleMembers.map((m) => {
                const config = ROLE_CONFIG[m.rol] ?? ROLE_CONFIG.VISOR;
                const Icon = config.icon;
                const isCurrentUser = m.user_id === user?.id;
                const isInactive = m.status === "inactive";
                const isMutating = toggleStatus.isPending && toggleStatus.variables?.memberId === m.id;

                return (
                  <TableRow key={m.id} className={isInactive ? "opacity-50" : ""}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs font-semibold bg-primary/10 text-primary">
                            {getInitials(m)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {getDisplayName(m)}
                            {isCurrentUser && (
                              <span className="ml-1.5 text-xs text-muted-foreground">(tú)</span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground truncate tabular-nums">
                            {m.email ?? "Sin correo"}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`gap-1 ${config.className}`}>
                        <Icon className="h-3 w-3" />
                        {config.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {isInactive ? (
                        <Badge
                          variant="outline"
                          className="gap-1 bg-destructive/10 text-destructive border-destructive/30"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-destructive" />
                          Inactivo
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="gap-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          Activo
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(m.created_at).toLocaleDateString("es-CO", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </TableCell>
                    <TableCell className="text-right">
                      {!isCurrentUser && (
                        <Button
                          variant={isInactive ? "outline" : "ghost"}
                          size="sm"
                          className={`gap-1.5 ${isInactive ? "text-emerald-600 hover:text-emerald-700" : "text-destructive hover:text-destructive"}`}
                          onClick={() => handleToggleClick(m)}
                          disabled={isMutating}
                        >
                          {isMutating ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : isInactive ? (
                            <>
                              <UserPlus className="h-3.5 w-3.5" />
                              Reactivar
                            </>
                          ) : (
                            <>
                              <UserMinus className="h-3.5 w-3.5" />
                              Desactivar
                            </>
                          )}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Deactivation confirmation dialog */}
      <AlertDialog open={!!confirmTarget} onOpenChange={(open) => !open && setConfirmTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desactivar miembro del equipo?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{confirmTarget?.email}</span> ya no
              podrá acceder a Kargonode, pero sus registros históricos se mantendrán para auditoría.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={toggleStatus.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeactivate}
              disabled={toggleStatus.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 gap-1.5"
            >
              {toggleStatus.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserMinus className="h-4 w-4" />
              )}
              Confirmar Desactivación
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <InviteMemberModal open={inviteOpen} onOpenChange={setInviteOpen} />

      {canEditOrg && orgId && (
        <OrgSettingsModal
          open={orgSettingsOpen}
          onOpenChange={setOrgSettingsOpen}
          orgId={orgId}
          initialNombre={activeOrg?.org_name ?? ""}
        />
      )}
    </main>
  );
}
